/**
 * AppMetricDashboard — GA4 web analytics view.
 *
 * Top: latest snapshot per property in a sortable / filterable
 *      DataGrid with CSV export.
 * Bottom: 30-day timeseries for the property selected in the grid.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ga4Api } from '../services/mrdsApi';
import {
  PageHeader,
  MetricCard,
  SectionCard,
  EmptyState,
  DataTable,
  CompareChart,
} from '../components/ui';
import { PLATFORM_COLORS } from '../theme';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));
const fmtSeconds = (s) => (s == null ? '—' : `${Number(s).toFixed(2)}s`);

export default function AppMetricDashboard() {
  const theme = useTheme();
  const [latest, setLatest] = useState({ rows: [], asOfDate: null, warning: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedAppPlatformId, setSelectedAppPlatformId] = useState('');
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);

  const [compareIds, setCompareIds] = useState([]);
  const COMPARE_MAX = 10;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    ga4Api
      .getLatest({ maxAgeDays: 7 })
      .then((res) => {
        if (cancelled) return;
        setLatest(res);
        if (res.rows && res.rows[0]) setSelectedAppPlatformId(res.rows[0].app_platform_id);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('GA4 verileri yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedAppPlatformId) {
      setSeries([]);
      return undefined;
    }
    let cancelled = false;
    setSeriesLoading(true);
    ga4Api
      .getTimeseries(selectedAppPlatformId, 30)
      .then((rows) => {
        if (cancelled) return;
        setSeries(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setSeries([]);
      })
      .finally(() => !cancelled && setSeriesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedAppPlatformId]);

  const totals = useMemo(() => {
    return (latest.rows || []).reduce(
      (acc, r) => ({
        properties: acc.properties + 1,
        active_users: acc.active_users + r.active_users,
        new_users: acc.new_users + r.new_users,
        event_counts: acc.event_counts + r.event_counts,
      }),
      { properties: 0, active_users: 0, new_users: 0, event_counts: 0 },
    );
  }, [latest]);

  const selectedProperty = useMemo(
    () => (latest.rows || []).find((r) => r.app_platform_id === selectedAppPlatformId),
    [latest, selectedAppPlatformId],
  );

  const gridRows = useMemo(
    () =>
      (latest.rows || []).map((r) => ({
        id: r.app_platform_id,
        property_name: r.property_name,
        app_extid: r.app_extid,
        active_users: r.active_users,
        new_users: r.new_users,
        returning_users: r.returning_users,
        views: r.views,
        avg_session: r.average_session_duration_seconds,
        engagement_per_user: r.user_engagement_duration_per_active_users_seconds,
        event_counts: r.event_counts,
        organic: r.user_acquisitions.organic_search,
        direct: r.user_acquisitions.direct,
        referrals: r.user_acquisitions.referrals,
      })),
    [latest],
  );

  const columns = [
    {
      field: 'property_name',
      headerName: 'Property',
      flex: 1.4,
      minWidth: 180,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'app_extid',
      headerName: 'Property ID',
      flex: 0.8,
      minWidth: 120,
      renderCell: ({ value }) => (
        <Chip label={value} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
      ),
    },
    { field: 'active_users', headerName: 'Active', type: 'number', flex: 0.7, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'new_users', headerName: 'New', type: 'number', flex: 0.7, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'returning_users', headerName: 'Returning', type: 'number', flex: 0.7, minWidth: 100, valueFormatter: ({ value }) => fmt(value) },
    { field: 'views', headerName: 'Views', type: 'number', flex: 0.7, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'avg_session', headerName: 'Avg session', type: 'number', flex: 0.8, minWidth: 110, valueFormatter: ({ value }) => fmtSeconds(value) },
    { field: 'engagement_per_user', headerName: 'Engagement/user', type: 'number', flex: 0.9, minWidth: 130, valueFormatter: ({ value }) => fmtSeconds(value) },
    { field: 'event_counts', headerName: 'Events', type: 'number', flex: 0.7, minWidth: 100, valueFormatter: ({ value }) => fmt(value) },
    { field: 'organic', headerName: 'Organic', type: 'number', flex: 0.6, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'direct', headerName: 'Direct', type: 'number', flex: 0.6, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
    { field: 'referrals', headerName: 'Referrals', type: 'number', flex: 0.6, minWidth: 90, valueFormatter: ({ value }) => fmt(value) },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Web Analytics (GA4)"
        subtitle="Latest GA4 snapshot per property + a 30-day trend for the selected one."
        rightSlot={
          latest.asOfDate && (
            <Chip
              label={`As of ${latest.asOfDate}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )
        }
      />

      {latest.warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {latest.warning}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label="Properties" value={fmt(totals.properties)} accent={PLATFORM_COLORS.web} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Active users" value={fmt(totals.active_users)} accent={PLATFORM_COLORS.android} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="New users" value={fmt(totals.new_users)} accent="#D97706" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Event count" value={fmt(totals.event_counts)} accent="#9333EA" />
        </Grid>
      </Grid>

      <SectionCard title="Latest snapshot" description="Click a row to view its 30-day trend." sx={{ mb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : gridRows.length === 0 ? (
          <EmptyState title="No GA4 properties returned." dense />
        ) : (
          <DataTable
            rows={gridRows}
            columns={columns}
            getRowId={(r) => r.id}
            height={520}
            onRowClick={(p) => setSelectedAppPlatformId(p.id)}
            initialState={{
              sorting: { sortModel: [{ field: 'active_users', sort: 'desc' }] },
            }}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            }}
            checkboxSelection
            rowSelectionModel={compareIds}
            onRowSelectionModelChange={(ids) => {
              if (ids.length > COMPARE_MAX) {
                setCompareIds(ids.slice(-COMPARE_MAX));
              } else {
                setCompareIds(ids);
              }
            }}
          />
        )}
      </SectionCard>

      {/* Compare card */}
      {compareIds.length >= 2 && (
        <Box sx={{ mb: 3 }}>
          <CompareChart
            title="Compare properties"
            description="Pick a metric to plot the ticked properties side by side."
            rows={gridRows.filter((r) => compareIds.includes(r.id))}
            labelField="property_name"
            metrics={[
              { field: 'active_users',        label: 'Active users' },
              { field: 'new_users',           label: 'New users' },
              { field: 'returning_users',     label: 'Returning users' },
              { field: 'views',               label: 'Views' },
              { field: 'event_counts',        label: 'Events' },
              { field: 'avg_session',         label: 'Avg session (s)',
                format: (v) => v == null ? '—' : `${Number(v).toFixed(2)}s` },
              { field: 'engagement_per_user', label: 'Engagement / user (s)',
                format: (v) => v == null ? '—' : `${Number(v).toFixed(2)}s` },
              { field: 'organic',             label: 'Organic search' },
              { field: 'direct',              label: 'Direct' },
              { field: 'referrals',           label: 'Referrals' },
            ]}
            rightSlot={
              <Chip
                label={`${compareIds.length} / ${COMPARE_MAX}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            }
          />
        </Box>
      )}

      <SectionCard
        title="30-day trend"
        rightSlot={
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Property</InputLabel>
            <Select
              label="Property"
              value={selectedAppPlatformId}
              onChange={(e) => setSelectedAppPlatformId(e.target.value)}
            >
              {(latest.rows || []).map((r) => (
                <MenuItem key={r.app_platform_id} value={r.app_platform_id}>
                  {r.property_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      >
        {seriesLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : series.length === 0 ? (
          <EmptyState title={`No timeseries for ${selectedProperty?.property_name || '—'}.`} dense />
        ) : (
          <Box sx={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="report_date" stroke={theme.palette.text.secondary} fontSize={12} />
                <YAxis stroke={theme.palette.text.secondary} fontSize={12} />
                <RechartsTooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="active_users" name="Active users" stroke={PLATFORM_COLORS.web} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="new_users" name="New users" stroke="#D97706" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="event_counts" name="Events" stroke={PLATFORM_COLORS.android} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </SectionCard>
    </Container>
  );
}
